import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { FaRocket, FaPlay, FaCheckCircle, FaTasks } from "react-icons/fa";
import SummaryCard from './SummaryCard';

// Styled components for summary layout
const SummaryContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 12px;
  margin-bottom: 16px;
  overflow-x: auto;
  padding: 4px 0;
`;

export const MarketingSummary = ({ onSummaryCardClick }) => {
  const marketing = useSelector(state => state.marketing);
  const currentBoardType = marketing.currentBoardType;
  const boardId = currentBoardType === 'convites' ? 2 : 1;
  const board = marketing.data ? marketing.data[boardId] : null;
  const isCurrentPeriod = marketing.currentPeriod ? marketing.currentPeriod.isCurrent : true;

  // Calculate summary metrics based on Marketing board data
  const summaryItems = useMemo(() => {
    if (!board || !board.lists) {
      if (currentBoardType === 'convites') {
        return [
          { title: 'Total Itens', icon: <FaRocket />, done: 0, undone: 0, color: '#6f42c1', tooltip: 'Total de itens criados', columnId: 'total', disabled: !isCurrentPeriod },
          { title: 'A Convidar', icon: <FaPlay />, done: 0, undone: 0, color: '#007bff', tooltip: 'Itens a convidar', columnId: 1, disabled: !isCurrentPeriod },
          { title: 'Convidando', icon: <FaTasks />, done: 0, undone: 0, color: '#17a2b8', tooltip: 'Itens convidados', columnId: 2, disabled: !isCurrentPeriod },
          { title: 'Agendado', icon: <FaTasks />, done: 0, undone: 0, color: '#ffc107', tooltip: 'Itens agendados', columnId: 3, disabled: !isCurrentPeriod },
          { title: 'Concluído', icon: <FaCheckCircle />, done: 0, undone: 0, color: '#28a745', tooltip: 'Itens concluídos', columnId: 4, disabled: false }
        ];
      } else {
        return [
          { title: 'Total Itens', icon: <FaRocket />, done: 0, undone: 0, color: '#6f42c1', tooltip: 'Total de itens criados', columnId: 'total', disabled: !isCurrentPeriod },
          { title: 'Pendente', icon: <FaPlay />, done: 0, undone: 0, color: '#007bff', tooltip: 'Itens pendentes', columnId: 1, disabled: !isCurrentPeriod },
          { title: 'Em Progresso', icon: <FaTasks />, done: 0, undone: 0, color: '#ffc107', tooltip: 'Itens em progresso', columnId: 2, disabled: !isCurrentPeriod },
          { title: 'Concluído', icon: <FaCheckCircle />, done: 0, undone: 0, color: '#28a745', tooltip: 'Itens concluídos', columnId: 3, disabled: false }
        ];
      }
    }

    const lists = board.lists;
    
    // Count items in each column based on board type
    const isConvitesBoard = currentBoardType === 'convites';
    
    let pendingCount, progressCount, completedCount, totalCount;
    
    if (isConvitesBoard) {
      // Convites board has 4 columns: 1-A convidar, 2-Convidando, 3-Agendado, 4-Concluído
      const aConvidarCount = (lists[1] && lists[1].cards && lists[1].cards.length) || 0;
      const convidadoCount = (lists[2] && lists[2].cards && lists[2].cards.length) || 0;
      const agendadoCount = (lists[3] && lists[3].cards && lists[3].cards.length) || 0;
      completedCount = (lists[4] && lists[4].cards && lists[4].cards.length) || 0;
      totalCount = aConvidarCount + convidadoCount + agendadoCount + completedCount;
    } else {
      // Missões board has 3 columns: 1-Pendente, 2-Em progresso, 3-Concluído
      pendingCount = (lists[1] && lists[1].cards && lists[1].cards.length) || 0;
      progressCount = (lists[2] && lists[2].cards && lists[2].cards.length) || 0;
      completedCount = (lists[3] && lists[3].cards && lists[3].cards.length) || 0;
      totalCount = pendingCount + progressCount + completedCount;
    }

    // Show all summary cards, but disable non-completed ones for historical periods
    if (isConvitesBoard) {
      return [
        { 
          title: 'Total Itens', 
          icon: <FaRocket />, 
          done: totalCount, 
          undone: 0, 
          color: '#6f42c1', 
          tooltip: 'Total de itens criados',
          columnId: 'total',
          disabled: !isCurrentPeriod
        },
        { 
          title: 'A Convidar', 
          icon: <FaPlay />, 
          done: (lists[1] && lists[1].cards && lists[1].cards.length) || 0, 
          undone: 0, 
          color: '#007bff', 
          tooltip: 'Itens a convidar',
          columnId: 1,
          disabled: !isCurrentPeriod
        },
        { 
          title: 'Convidando', 
          icon: <FaTasks />, 
          done: (lists[2] && lists[2].cards && lists[2].cards.length) || 0, 
          undone: 0, 
          color: '#17a2b8', 
          tooltip: 'Itens convidados',
          columnId: 2,
          disabled: !isCurrentPeriod
        },
        { 
          title: 'Agendado', 
          icon: <FaTasks />, 
          done: (lists[3] && lists[3].cards && lists[3].cards.length) || 0, 
          undone: 0, 
          color: '#ffc107', 
          tooltip: 'Itens agendados',
          columnId: 3,
          disabled: !isCurrentPeriod
        },
        { 
          title: 'Concluído', 
          icon: <FaCheckCircle />, 
          done: completedCount, 
          undone: 0, 
          color: '#28a745', 
          tooltip: 'Itens concluídos',
          columnId: 4,
          disabled: false
        }
      ];
    } else {
      return [
        { 
          title: 'Total Itens', 
          icon: <FaRocket />, 
          done: totalCount, 
          undone: 0, 
          color: '#6f42c1', 
          tooltip: 'Total de itens criados',
          columnId: 'total',
          disabled: !isCurrentPeriod
        },
        { 
          title: 'Pendente', 
          icon: <FaPlay />, 
          done: pendingCount, 
          undone: 0, 
          color: '#007bff', 
          tooltip: 'Itens pendentes',
          columnId: 1,
          disabled: !isCurrentPeriod
        },
        { 
          title: 'Em Progresso', 
          icon: <FaTasks />, 
          done: progressCount, 
          undone: 0, 
          color: '#ffc107', 
          tooltip: 'Itens em progresso',
          columnId: 2,
          disabled: !isCurrentPeriod
        },
        { 
          title: 'Concluído', 
          icon: <FaCheckCircle />, 
          done: completedCount, 
          undone: 0, 
          color: '#28a745', 
          tooltip: 'Itens concluídos',
          columnId: 3,
          disabled: false
        }
      ];
    }
  }, [board, isCurrentPeriod, currentBoardType]);


  return (
    <SummaryContainer>
      {summaryItems.map((item) => (
        <SummaryCard
          key={item.title}
          title={item.title}
          icon={item.icon}
          done={item.done}
          undone={item.undone}
          color={item.color}
          tooltip={item.tooltip}
          disabled={item.disabled}
          onClick={() => onSummaryCardClick && onSummaryCardClick(item.columnId)}
        />
      ))}
    </SummaryContainer>
  );
};

export default MarketingSummary;